import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { SettingsSrvAbstract } from '@base/abstracts/services/settings.abstract';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { ModalService } from '@base/services/private/modal.service';
import { Router } from '@angular/router';
import { SessionService } from '@base/services/private/session.service';
import { finalize, Subscription } from 'rxjs';
import { GlobalService } from '@base/services/private/global.service';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { ThemeService } from '@base/services/private/theme.service';
import { TranslateService } from '@base/services/private/translate.service';
import { CookieService } from 'ngx-cookie-service';

enum ActiveButton {
  REMOVE_ACCOUNT = 'remove-account',
}

@Component({
  selector: 'app-modal-remove-account',
  imports: [forwardRef(() => TranslatePipe), ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent],
  templateUrl: './modal-remove-account.component.html',
  styleUrl: './modal-remove-account.component.scss',
})
export class ModalRemoveAccountComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly modalSrv = inject(ModalService);
  private readonly router = inject(Router);
  private readonly sessionSrv = inject(SessionService);
  private settingsSrv!: SettingsSrvAbstract;
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalSrv = inject(GlobalService);
  private readonly themeSrv = inject(ThemeService);
  private readonly translateSrv = inject(TranslateService);
  private readonly cookieSrv = inject(CookieService);

  @Input() modalId = '';

  httpRequestObserver$: Subscription | null = null;
  countDownAccountRemoval = 5;
  confirmDeleteText = `${this.translateSrv.translate('YES_DELETE')}`;
  isHttpRequestRunning = false;
  isBgDarkMode = this.themeSrv.isBgDarkMode;
  errorMessage = '';

  ActiveButton = ActiveButton;
  activeButton = '';

  ngAfterViewInit(): void {
    (async () => {
      await this.loadServices();
    })();
  }

  ngOnInit(): void {
    this.listenHttpRequestState();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Dismisses the current modal using `activeModal.close()`.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Loads necessary services for the app.
   *
   * @returns Promise<void> Resolves when all services are loaded.
   */
  private async loadServices(): Promise<void> {
    try {
      const services = await Promise.all([this.dynamicImportLoaderSrv.loadService(WhiteLabel.SERVICES.Settings)]);

      if (services) {
        const [settingsSrv] = services;
        this.settingsSrv = settingsSrv as SettingsSrvAbstract;

        this.startCountDown();
      } else {
        throw new Error('Services could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  }

  /**
   * function to start countdown for button disable before actual account removal
   */
  startCountDown() {
    this.confirmDeleteText = `${this.translateSrv.translate('YES_DELETE')} (${this.countDownAccountRemoval})`;
    const countDownAccountRemovalTimer = setInterval(() => {
      if (this.countDownAccountRemoval < 1) {
        clearInterval(countDownAccountRemovalTimer);
        this.confirmDeleteText = `${this.translateSrv.translate('YES_DELETE')}`;
      } else {
        this.countDownAccountRemoval -= 1;
        this.confirmDeleteText = `${this.translateSrv.translate('YES_DELETE')} (${this.countDownAccountRemoval})`;
      }
    }, 1000);
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Initiates account removal using `settingsSrv.removeMyAccount`.
   * Logs the successful response to the console.
   *
   * @remarks Errors and completion events are not handled.
   */
  delete(): void {
    this.errorMessage = '';
    this.activeButton = ActiveButton.REMOVE_ACCOUNT;
    this.settingsSrv
      .removeMyAccount()
      .pipe(
        finalize(() => {
          this.activeButton = '';
        })
      )
      .subscribe({
        next: (res) => {
          if (res.status === StatusResponse.SUCCESS) {
            this.close();
            this.sessionSrv.clear();
            this.cookieSrv.deleteAll();
            this.router.navigate(['/']);
            window.location.reload();
          } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
            // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
            this.showModalWarning();
          } else {
            console.error('Error removing account:', res.status);
            this.errorMessage = 'SOMETHING_WENT_WRONG';
          }
          this.triggerChangeDetection();
        },
        error: (err) => {
          console.error('Error removing account:', err);
          this.errorMessage = 'SOMETHING_WENT_WRONG';
          this.triggerChangeDetection();
        },
      });
  }

  /**
   * Triggers change detection for the component.
   * @returns void
   */
  triggerChangeDetection() {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
